import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import Paper from '@mui/material/Paper';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  ListItemText,
  FormControlLabel,
  Switch,
  styled,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Typography,
  createTheme,
  useMediaQuery,
} from '@mui/material';
import './style.css';
import LeadStore from '../../store/LeadStore';
import { isEmpty } from '../../utils/helper';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export const EditResidentialBuildingDetails = observer(({ leadId, details, handleEditDetails }) => {
  const theme = createTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const CustomFormControlLabel = styled(FormControlLabel)({
    '& .MuiFormControlLabel-label': {
      fontSize: '0.75rem',
      color: '#0009',
    },
  });

  const [formData, setFormData] = useState({
    id: details?.clientResidentialBuildingDetailId ?? null,
    leadId,
    groundFloor: details?.groundFloor,
    livingRoom: details?.livingRoom,
    commonToilet: details?.commonToilet,
    kitchen: details?.kitchen,
    prayerRoom: details?.prayerRoom ?? false,
    staircase: details?.staircase,
    lift: details?.lift,
    bedRoom: details?.bedRoom?.split(','),
    bedRoomAdditionalDetails: details?.bedRoomAdditionalDetails,
    utility: details?.utility ?? false,
    laundry: details?.laundry?.split(','),
    gym: details?.gym?.split(','),
    homeTheater: details?.homeTheater?.split(','),
    additionalDetails: details?.additionalDetails,
    createdBy: details?.CreatedBy,
    createdDate: details?.createdDate,
    updatedBy: details?.updatedBy,
    updatedDate: details?.updatedDate,
  });

  const [errors, setErrors] = useState({});

  const [errorMessages, setErrorMessages] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'prayerRoom' || name === 'utility') {
      setFormData({ ...formData, [name]: e.target.checked });
    } else {
      setFormData({ ...formData, [name]: value });
    }

    // Perform validation
    setErrors({ ...errors, [name]: isEmpty(value) });
  };

  const handleUpdateClientResidentialBuildingDetailSubmit = (e) => {
    e.preventDefault();

    const updatedErrors = {};
    setErrors(updatedErrors);

    if (!Object.values(updatedErrors).some((error) => error === true)) {
      const payload = { ...formData };
      payload.bedRoom = payload.bedRoom?.join(',');
      payload.gym = payload.gym?.join(',');
      payload.homeTheater = payload.homeTheater?.join(',');
      payload.laundry = payload.laundry?.join(',');
      isEmpty(formData.id)
        ? LeadStore.createClientResidentialBuildingDetail(payload)
        : LeadStore.updateClientResidentialBuildingDetail(payload);
    }
  };

  useEffect(() => {
    LeadStore.updateClientResidentialBuildingDetailStatus && handleEditDetails();
  }, [LeadStore.updateClientResidentialBuildingDetailStatus]);

  const isContactNumberError = (value, name) => {
    if (isEmpty(value)) {
      return true;
    } else if (!/^\d{0,10}$/.test(value)) {
      setErrorMessages({
        ...errors,
        [name]: 'Please enter valid 10 digit number',
      });
      return true;
    } else {
      return false;
    }
  };

  const isNumberError = (value, name) => {
    if (isEmpty(value)) {
      return false;
    } else if (!/^\d{0,10}$/.test(value)) {
      setErrorMessages({
        ...errors,
        [name]: 'Please enter valid 10 digit number',
      });
      return true;
    } else {
      return false;
    }
  };

  const groundFloorList = [
    { id: 1, name: 'Parking' },
    { id: 2, name: 'Foyer' },
  ];

  const livingRoomList = [
    { id: 1, name: '3-5 People' },
    { id: 2, name: '5-8 People' },
    { id: 3, name: '8-10 People' },
    { id: 4, name: 'More than 10 people' },
  ];

  const commonToiletList = [
    { id: 1, name: 'Powder (Sink + Commode)' },
    { id: 2, name: 'Bathroom (Sink + Commode + Shower)' },
  ];

  const kitchenList = [
    { id: 1, name: 'Open Kitchen' },
    { id: 2, name: 'Semi-open Kitchen' },
    { id: 3, name: 'Closed Kitchen' },
    { id: 4, name: 'Store Room' },
    { id: 5, name: 'Utility' },
  ];

  const staircaseList = [
    { id: 1, name: 'Internal' },
    { id: 2, name: 'External' },
    { id: 3, name: 'Both' },
    { id: 4, name: 'None' },
  ];

  const liftList = [
    { id: 1, name: '1-2 Pax' },
    { id: 2, name: '2-4 Pax' },
    { id: 3, name: '4-6 Pax' },
    { id: 4, name: '6-8 Pax' },
  ];

  const bedRoomList = [
    { id: 1, name: 'Attached Bathroom' },
    { id: 2, name: 'Walk-in Wardrobe' },
    { id: 3, name: 'Balcony / Site-out' },
    { id: 4, name: 'None' },
  ];

  const laundryList = [
    { id: 1, name: 'Attached Bathroom' },
    { id: 3, name: 'Balcony / Site-out' },
  ];

  const gymList = [
    { id: 1, name: 'Attached Bathroom' },
    { id: 3, name: 'Balcony / Site-out' },
  ];

  const homeTheaterList = [
    { id: 1, name: 'Attached Bathroom' },
    { id: 3, name: 'Balcony / Site-out' },
  ];

  return (
    <Paper sx={{ px: '30px', py: '20px', pb: '40px' }}>
      <Box
        id="client-info-form"
        component="form"
        noValidate
        autoComplete="off"
        onSubmit={(event) => {
          event.preventDefault();
          handleUpdateClientResidentialBuildingDetailSubmit(event);
        }}
      >
        <Stack spacing={4}>
          <Stack spacing={3}>
            <Grid rowGap={2} container>
              <Grid item xs={12} sm={6} md={6} lg={6} sx={{ pr: isSmallScreen ? '0px' : '10px' }}>
                <Stack direction={'column'} spacing={3}>
                  <FormControl variant="standard" error={Boolean(errors.groundFloor)}>
                    <InputLabel id="groundFloorLabel" shrink={true}>
                      Ground Floor
                    </InputLabel>
                    <Select
                      labelId="groundFloorLabel"
                      id="groundFloor"
                      name="groundFloor"
                      value={formData.groundFloor ?? ''}
                      onChange={(e) => {
                        const {
                          target: { value },
                        } = e;
                        setFormData({ ...formData, ['groundFloor']: value });
                        setErrors({
                          ...errors,
                          ['groundFloor']: isEmpty(value),
                        });
                      }}
                      label="Ground Floor"
                      MenuProps={MenuProps}
                    >
                      {groundFloorList.map((site) => (
                        <MenuItem key={site.id} value={site.name}>
                          <ListItemText primary={site.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl variant="standard" error={Boolean(errors.livingRoom)}>
                    <InputLabel id="livingRoomLabel" shrink={true}>
                      Living Room
                    </InputLabel>
                    <Select
                      labelId="livingRoomLabel"
                      id="livingRoom"
                      name="livingRoom"
                      value={formData.livingRoom ?? ''}
                      onChange={(e) => {
                        const {
                          target: { value },
                        } = e;
                        setFormData({ ...formData, ['livingRoom']: value });
                        setErrors({
                          ...errors,
                          ['livingRoom']: isEmpty(value),
                        });
                      }}
                      label="Living Room"
                      MenuProps={MenuProps}
                    >
                      {livingRoomList.map((site) => (
                        <MenuItem key={site.id} value={site.name}>
                          <ListItemText primary={site.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl variant="standard" error={Boolean(errors.commonToilet)}>
                    <InputLabel id="commonToiletLabel" shrink={true}>
                      Common Toilet
                    </InputLabel>
                    <Select
                      labelId="commonToiletLabel"
                      id="commonToilet"
                      name="commonToilet"
                      value={formData.commonToilet ?? ''}
                      onChange={(e) => {
                        const {
                          target: { value },
                        } = e;
                        setFormData({ ...formData, ['commonToilet']: value });
                        setErrors({
                          ...errors,
                          ['commonToilet']: isEmpty(value),
                        });
                      }}
                      label="Common Toilet"
                      MenuProps={MenuProps}
                    >
                      {commonToiletList.map((site) => (
                        <MenuItem key={site.id} value={site.name}>
                          <ListItemText primary={site.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl variant="standard" error={Boolean(errors.kitchen)}>
                    <InputLabel id="kitchenLabel" shrink={true}>
                      Kitchen
                    </InputLabel>
                    <Select
                      labelId="kitchenLabel"
                      id="kitchen"
                      name="kitchen"
                      value={formData.kitchen ?? ''}
                      onChange={(e) => {
                        const {
                          target: { value },
                        } = e;
                        setFormData({ ...formData, ['kitchen']: value });
                        setErrors({ ...errors, ['kitchen']: isEmpty(value) });
                      }}
                      label="Kitchen"
                      MenuProps={MenuProps}
                    >
                      {kitchenList.map((site) => (
                        <MenuItem key={site.id} value={site.name}>
                          <ListItemText primary={site.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <CustomFormControlLabel
                    control={
                      <Switch checked={formData.prayerRoom} size="medium" onChange={handleChange} name="prayerRoom" />
                    }
                    label="Prayer room"
                  />
                  <FormControl variant="standard" error={Boolean(errors.staircase)}>
                    <InputLabel id="staircaseLabel" shrink={true}>
                      Staircase
                    </InputLabel>
                    <Select
                      labelId="staircaseLabel"
                      id="staircase"
                      name="staircase"
                      value={formData.staircase ?? ''}
                      onChange={(e) => {
                        const {
                          target: { value },
                        } = e;
                        setFormData({ ...formData, ['staircase']: value });
                        setErrors({
                          ...errors,
                          ['staircase']: isEmpty(value),
                        });
                      }}
                      label="Staircase"
                      MenuProps={MenuProps}
                    >
                      {staircaseList.map((site) => (
                        <MenuItem key={site.id} value={site.name}>
                          <ListItemText primary={site.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl variant="standard" error={Boolean(errors.lift)}>
                    <InputLabel id="liftLabel" shrink={true}>
                      Lift
                    </InputLabel>
                    <Select
                      labelId="liftLabel"
                      id="lift"
                      name="lift"
                      value={formData.lift ?? ''}
                      onChange={(e) => {
                        const {
                          target: { value },
                        } = e;
                        setFormData({ ...formData, ['lift']: value });
                        setErrors({ ...errors, ['lift']: isEmpty(value) });
                      }}
                      label="Lift"
                      MenuProps={MenuProps}
                    >
                      {liftList.map((site) => (
                        <MenuItem key={site.id} value={site.name}>
                          <ListItemText primary={site.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={6} sx={{ pl: isSmallScreen ? '0px' : '10px' }}>
                <Stack direction={'column'} spacing={3}>
                  <FormControl variant="standard" error={Boolean(errors.bedRoom)}>
                    <InputLabel id="bedRoomLabel" shrink={true}>
                      Bedroom
                    </InputLabel>
                    <Select
                      labelId="bedRoomLabel"
                      id="bedRoom"
                      name="bedRoom"
                      value={formData.bedRoom ?? []}
                      multiple
                      onChange={(e) => {
                        const {
                          target: { value },
                        } = e;
                        setFormData({
                          ...formData,
                          ['bedRoom']: typeof value === 'string' ? value.split(',') : value,
                        });
                        setErrors({ ...errors, ['bedRoom']: isEmpty(value) });
                      }}
                      label="Bedroom"
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip key={value} label={value} />
                          ))}
                        </Box>
                      )}
                      MenuProps={MenuProps}
                    >
                      {bedRoomList.map((site) => (
                        <MenuItem key={site.id} value={site.name}>
                          <Checkbox checked={formData.bedRoom?.indexOf(site.name) > -1} />
                          <ListItemText primary={site.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    label="Bedroom Additional Details"
                    id="bedRoomAdditionalDetails"
                    size="small"
                    variant="standard"
                    name="bedRoomAdditionalDetails"
                    multiline
                    rows={1}
                    value={formData.bedRoomAdditionalDetails ?? ''}
                    onChange={(e) => handleChange(e)}
                    error={Boolean(errors.bedRoomAdditionalDetails)}
                    sx={{ fontSize: '12px' }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  <CustomFormControlLabel
                    control={<Switch checked={formData.utility} size="medium" onChange={handleChange} name="utility" />}
                    label="Utility"
                  />
                  <FormControl variant="standard" error={Boolean(errors.laundry)}>
                    <InputLabel id="laundryLabel" shrink={true}>
                      Laundry
                    </InputLabel>
                    <Select
                      labelId="laundryLabel"
                      id="laundry"
                      name="laundry"
                      value={formData.laundry ?? []}
                      multiple
                      onChange={(e) => {
                        const {
                          target: { value },
                        } = e;
                        setFormData({
                          ...formData,
                          ['laundry']: typeof value === 'string' ? value.split(',') : value,
                        });
                        setErrors({ ...errors, ['laundry']: isEmpty(value) });
                      }}
                      label="Laundry"
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip key={value} label={value} />
                          ))}
                        </Box>
                      )}
                      MenuProps={MenuProps}
                    >
                      {laundryList.map((site) => (
                        <MenuItem key={site.id} value={site.name}>
                          <Checkbox checked={formData.laundry?.indexOf(site.name) > -1} />
                          <ListItemText primary={site.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl variant="standard" error={Boolean(errors.gym)}>
                    <InputLabel id="gymLabel" shrink={true}>
                      Gym
                    </InputLabel>
                    <Select
                      labelId="gymLabel"
                      id="gym"
                      name="gym"
                      value={formData.gym ?? []}
                      multiple
                      onChange={(e) => {
                        const {
                          target: { value },
                        } = e;
                        setFormData({
                          ...formData,
                          ['gym']: typeof value === 'string' ? value.split(',') : value,
                        });
                        setErrors({ ...errors, ['gym']: isEmpty(value) });
                      }}
                      label="Gym"
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip key={value} label={value} />
                          ))}
                        </Box>
                      )}
                      MenuProps={MenuProps}
                    >
                      {gymList.map((site) => (
                        <MenuItem key={site.id} value={site.name}>
                          <Checkbox checked={formData.gym?.indexOf(site.name) > -1} />
                          <ListItemText primary={site.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl variant="standard" error={Boolean(errors.homeTheater)}>
                    <InputLabel id="homeTheaterLabel" shrink={true}>
                      Home Theatre
                    </InputLabel>
                    <Select
                      labelId="homeTheaterLabel"
                      id="homeTheater"
                      name="homeTheater"
                      value={formData.homeTheater ?? []}
                      multiple
                      onChange={(e) => {
                        const {
                          target: { value },
                        } = e;
                        setFormData({
                          ...formData,
                          ['homeTheater']: typeof value === 'string' ? value.split(',') : value,
                        });
                        setErrors({
                          ...errors,
                          ['homeTheater']: isEmpty(value),
                        });
                      }}
                      label="Home Theatre"
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip key={value} label={value} />
                          ))}
                        </Box>
                      )}
                      MenuProps={MenuProps}
                    >
                      {homeTheaterList.map((site) => (
                        <MenuItem key={site.id} value={site.name}>
                          <Checkbox checked={formData.homeTheater?.indexOf(site.name) > -1} />
                          <ListItemText primary={site.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    label="Additional Details"
                    id="additionalDetails"
                    size="small"
                    variant="standard"
                    name="additionalDetails"
                    multiline
                    rows={1}
                    value={formData.additionalDetails ?? ''}
                    onChange={(e) => handleChange(e)}
                    error={Boolean(errors.additionalDetails)}
                    sx={{ fontSize: '12px' }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Stack>
              </Grid>
            </Grid>
          </Stack>
          <Stack direction={'row'} sx={{ ml: 'auto' }} alignSelf={isSmallScreen ? '' : 'end'} spacing={2}>
            <Button variant="outlined" color="inherit" fullWidth size="small" onClick={() => handleEditDetails(false)}>
              Cancel
            </Button>
            <Button variant="contained" type="submit" fullWidth color="primary" size="small">
              Update
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Paper>
  );
});
